import * as yup from 'yup';

/**
 * A ticket holder the buyer has used on a previous order, offered by the saved-holders sheet
 * (TZ-CO-5). Shape pinned by the *Saved Holders Read* API contract.
 */
export type SavedHolder = yup.InferType<typeof savedHolderSchema>;

/**
 * `phone` is E.164; `lastUsedAt` is an ISO timestamp.
 *
 * **`id` is a number on the wire** (`1`), and is **normalized to a string here**. It is opaque to
 * the client — a list key and a radio value, never parsed — and `RadioGroup` hands its `onChange` a
 * string, so a string is the form the UI actually needs. Casting at the boundary means a later
 * change of id type on the server cannot reach the sheet.
 *
 * That matters because it already happened: the *Saved Holders Read* doc specified `id: "sh_…"` and
 * this schema enforced the prefix, so the live numeric id **dropped every row and the sheet
 * rendered permanently empty** — a total, silent failure of the web contact path, visible only as
 * the Sentry warning `parseSavedHolders` reports. Validating the *format* of an opaque identifier
 * bought nothing and cost the whole feature; presence is all that is actually required.
 */
export const savedHolderSchema = yup.object({
  id: yup.string().required().min(1),
  name: yup.string().required(),
  phone: yup.string().required(),
  lastUsedAt: yup
    .string()
    .required()
    .test('iso-datetime', 'lastUsedAt must be an ISO datetime', (value) =>
      Boolean(value && !Number.isNaN(Date.parse(value))),
    ),
});

/**
 * The signed-in buyer's own contact, returned alongside the list as `me`.
 *
 * `name` is **nullable** — the live endpoint returns `null` for a buyer who has never given one —
 * so this is not a `SavedHolder`: it has no `id` and no `lastUsedAt`, and it cannot be assumed to
 * satisfy the holder form. Ticket 1 is auto-assigned from it, filling whatever is usable.
 */
export const savedHolderMeSchema = yup.object({
  name: yup.string().nullable().default(null),
  phone: yup.string().required(),
});

export type SavedHolderMe = yup.InferType<typeof savedHolderMeSchema>;

export type ParsedSavedHolders = {
  /** The buyer themselves, when the response carries a usable `me`; `undefined` otherwise. */
  me?: SavedHolderMe;
  holders: SavedHolder[];
  /** Rows that failed the contract. Non-zero means the API and this schema disagree. */
  droppedCount: number;
};

/** `me` is optional and non-blocking: an absent or malformed one simply means no auto-assign. */
const parseMe = (raw: unknown): SavedHolderMe | undefined => {
  if (raw == null || typeof raw !== 'object') return undefined;
  try {
    return savedHolderMeSchema.validateSync(raw, { strict: false, stripUnknown: true });
  } catch {
    return undefined;
  }
};

/**
 * Validates a `GET saved-holders` body against the API contract.
 *
 * Deliberately **per-row**: a single malformed entry drops itself rather than hiding the buyer's
 * other holders, and anything unrecognisable at the top level yields an empty list. The endpoint
 * is never on the critical path — an empty result means "sheet shows its empty state and manual
 * entry carries on", never an error surface.
 *
 * Checks the **contract only**. Whether a holder can actually be used by the checkout form is a
 * separate question, decided by `useSavedHolders` where the form's own rules live — this module
 * stays free of checkout-form concerns.
 *
 * Pure: `droppedCount` is reported back so the caller can raise the alarm, rather than logging
 * from inside a parser.
 *
 * `me` and `holders` are independent: a malformed list still yields a usable `me` (so ticket 1 is
 * still auto-assigned), and a malformed `me` still yields the list.
 *
 * @param body - Unvalidated response body.
 * @returns The buyer's own contact if present, contract-valid holders in API order, and how many
 *   rows were discarded.
 */
export const parseSavedHolders = (body: unknown): ParsedSavedHolders => {
  const me = parseMe((body as { me?: unknown })?.me);
  const rows = (body as { holders?: unknown })?.holders;
  if (!Array.isArray(rows)) return { me, holders: [], droppedCount: 0 };

  const holders = rows.reduce<SavedHolder[]>((valid, row) => {
    try {
      valid.push(savedHolderSchema.validateSync(row, { strict: false, stripUnknown: true }));
    } catch {
      // Malformed row — skip it and keep the rest.
    }
    return valid;
  }, []);

  return { me, holders, droppedCount: rows.length - holders.length };
};
